import { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet';
import emailjs from '@emailjs/browser';
import heroImage from '../assets/hero.webp';
import AppointmentModal from '../components/AppointmentModal';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);

  // Animation controls
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    } else {
      controls.start('hidden');
    }
  }, [controls, inView]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setErrorMsg('Please fill all required fields (Name, Email, Message).');
      return;
    }

    setIsLoading(true);
    setErrorMsg('');
    setSuccessMsg('');

    emailjs.send(
      'service_ne6qnnx',
      'template_2egrltl',
      formData,
      'EIJ9uuFXCIiT5OIN3'
    )
      .then(() => {
        setSuccessMsg('Thank you! Your message has been sent successfully.');
        setFormData({ name: '', email: '', phone: '', message: '' });
      })
      .catch((err) => {
        setErrorMsg('Failed to send message. Please try again later.');
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: "beforeChildren"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        type: "spring",
        stiffness: 100
      }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeInOut"
      }
    }
  };

  return (
    <main className="min-h-screen bg-gray-50" ref={ref}>
      {/* SEO Optimization with React Helmet */}
      <Helmet>
        <title>Contact Us | The Healing - Connect with Our Healthcare Experts</title>
        <meta
          name="description"
          content="Get in touch with The Healing healthcare team for consultations, appointments, or general inquiries. We're committed to your health and well-being in Gaya, Bihar."
        />
        <meta
          name="keywords"
          content="The Healing contact, healthcare Gaya, book appointment, doctor consultation, medical center Bihar"
        />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thehealing.in/contact" />
        <meta property="og:title" content="Contact Us | The Healing - Connect with Our Healthcare Experts" />
        <meta
          property="og:description"
          content="Get in touch with The Healing healthcare team for consultations, appointments, or general inquiries. We're committed to your health and well-being in Gaya, Bihar."
        />
        <meta property="og:image" content="https://thehealing.co.in/static/media/hero.webp" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://thehealing.co.in/contact" />
        <meta property="twitter:title" content="Contact Us | The Healing - Connect with Our Healthcare Experts" />
        <meta
          property="twitter:description"
          content="Get in touch with The Healing healthcare team for consultations, appointments, or general inquiries. We're committed to your health and well-being in Gaya, Bihar."
        />
        <meta property="twitter:image" content="https://thehealing.co.in/static/media/hero.webp" />
      </Helmet>


      {/* Appointment Modal */}
      <AppointmentModal
        isOpen={isAppointmentOpen}
        onClose={() => setIsAppointmentOpen(false)}
      />

      {/* Hero Section */}
      <section className="relative text-white py-28 overflow-hidden mt-0 min-h-[500px]">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Dental clinic reception area"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-blue-900 opacity-60 mix-blend-multiply"></div>
        </div>

        <motion.div
          className="container mx-auto px-4 text-center relative z-10"
          initial="hidden"
          animate={controls}
          variants={containerVariants}
        >
          <motion.h1
            variants={itemVariants}
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
          >
            <span className="bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Let's Connect With Us
            </span>
          </motion.h1>
          <motion.p
            variants={itemVariants}
            className="text-xl md:text-2xl max-w-3xl mx-auto mb-8 opacity-90"
          >
            We're here to help and answer any questions you might have. Reach out and we'll respond as soon as possible.
          </motion.p>
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row justify-center gap-4"
          >
            <motion.a
              href="tel:+919430003381"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-white text-blue-600 px-6 py-3 rounded-full font-medium hover:bg-blue-50 transition duration-300 shadow-lg hover:shadow-xl"
            >
              Call Now
            </motion.a>
            <motion.button
              onClick={() => setIsAppointmentOpen(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="border-2 border-white px-6 py-3 rounded-full font-medium hover:bg-white hover:text-blue-600 transition duration-300 shadow-lg hover:shadow-xl"
            >
              Book Appointment
            </motion.button>
          </motion.div>
        </motion.div>
      </section>

      {/* Contact Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={controls}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12"
          >
            {/* Contact Form */}
            <motion.div
              variants={itemVariants}
              className="bg-white p-6 sm:p-8 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100"
            >
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                <span className="bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">
                  Send Us a Message
                </span>
              </h2>

              {successMsg && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-green-100 text-green-700 rounded-lg"
                >
                  {successMsg}
                </motion.div>
              )}
              {errorMsg && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg"
                >
                  {errorMsg}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="relative">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    className="w-full px-4 py-3 pl-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300 hover:border-blue-300"
                    required
                  />
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400">👤</span>
                </div>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    className="w-full px-4 py-3 pl-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300 hover:border-blue-300"
                    required
                  />
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400">✉️</span>
                </div>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number"
                    className="w-full px-4 py-3 pl-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300 hover:border-blue-300"
                  />
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-blue-400">📱</span>
                </div>
                <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    rows="5"
                    className="w-full px-4 py-3 pl-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all duration-300 hover:border-blue-300"
                    required
                  ></textarea>
                  <span className="absolute left-4 top-4 text-blue-400">✏️</span>
                </div>
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isLoading}
                  className={`w-full bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center shadow-md hover:shadow-lg ${isLoading ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                >
                  {isLoading ? 'Sending...' : <>Send Message <span className="ml-2">✈️</span></>}
                </motion.button>
              </form>
            </motion.div>

            {/* Contact Info Cards */}
            <div className="space-y-6">
              <motion.div
                variants={itemVariants}
                className="bg-white p-6 rounded-xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300"
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  <span className="bg-gradient-to-r from-blue-500 to-cyan-400 bg-clip-text text-transparent">
                    Our Location
                  </span>
                </h2>
                <div className="overflow-hidden rounded-lg h-64">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14487.13262432955!2d85.2435917!3d24.802877700000003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f32043abc53c17%3A0x2016958e73dd94b3!2sSinha%20Colony%2C%20Wazirganj%2C%20Bihar%20805131!5e0!3m2!1sen!2sin!4v1753472089989!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    title="Our Location on Google Maps"
                    className="rounded-lg hover:scale-[1.01] transition-transform duration-300"
                  ></iframe>
                </div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Address Card */}
                <motion.div
                  variants={itemVariants}
                  className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-blue-100 to-cyan-100 p-3 rounded-full text-blue-600">
                      <span className="text-xl">📍</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">Address</h3>
                      <p className="text-gray-600">Wazirganj, Gaya</p>
                      <p className="text-gray-600">Bihar, India - 803051</p>
                    </div>
                  </div>
                </motion.div>

                {/* Phone Card */}
                <motion.div
                  variants={itemVariants}
                  className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-blue-100 to-cyan-100 p-3 rounded-full text-blue-600">
                      <span className="text-xl">📞</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">Phone</h3>
                      <p>
                        <a href="tel:+919430003381" className="text-blue-500 hover:underline">
                          +91 9430003381
                        </a>
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Email Card */}
                <motion.div
                  variants={itemVariants}
                  className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-blue-100 to-cyan-100 p-3 rounded-full text-blue-600">
                      <span className="text-xl">✉️</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">Email</h3>
                      <p>
                        <a href="mailto:info@pcds.co.in" className="text-blue-500 hover:underline">
                          info@pcds.co.in
                        </a>
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Working Hours */}
                <motion.div
                  variants={itemVariants}
                  className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-blue-100 to-cyan-100 p-3 rounded-full text-blue-600">
                      <span className="text-xl">⏰</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-gray-800">Working Hours</h3>
                      <p className="text-gray-600">Mon-Fri: 8:00 AM - 8:00 PM</p>
                      <p className="text-gray-600">Sat: 9:00 AM - 5:00 PM</p>
                      <p className="text-gray-600">Sun: Emergency only</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Contact;